import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { getProviderDocumentsForReview } from "../../utils/documentUploadAdminService";
import DocumentReviewCard from "./DocumentReviewCard";

const DOCUMENT_GROUPS = [
  {
    key: "passport",
    label: "Passport",
    aliases: ["passport", "id_proof"],
    legacyKeys: ["passportUrl", "passport", "idProofUrl", "idProof"],
    identity: true,
  },
  {
    key: "drivingLicence",
    label: "Driving Licence",
    aliases: ["drivingLicence", "drivingLicense", "driving_licence", "driving_license"],
    legacyKeys: ["drivingLicenceUrl", "drivingLicenseUrl", "drivingLicence", "drivingLicense"],
    identity: true,
  },
  {
    key: "resume",
    label: "Resume/CV",
    aliases: ["resume", "resume_cv", "cv"],
    legacyKeys: ["resumeUrl", "resume", "cvUrl", "cv"],
  },
  {
    key: "certificates",
    label: "Certificates",
    aliases: ["certificates", "certificate"],
    legacyKeys: ["certificatesUrl", "certificates", "certificateUrl", "certificate"],
  },
  {
    key: "verificationVideo",
    label: "Verification Video",
    aliases: ["verificationVideo", "verification_video"],
    legacyKeys: ["verificationVideoUrl", "verificationVideo"],
  },
];

const DOCUMENT_KEY_BY_ALIAS = DOCUMENT_GROUPS.reduce((acc, group) => {
  group.aliases.forEach((alias) => {
    acc[alias] = group.key;
  });
  return acc;
}, {});

const normalizeDocumentKey = (value) =>
  DOCUMENT_KEY_BY_ALIAS[String(value || "")] || String(value || "");

const ACTIVE_DOCUMENT_STATUSES = new Set([
  "uploaded",
  "submitted",
  "approved",
  "pending_review",
  "rejected",
  "reupload_required",
]);

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const getDocumentSortTime = (document) =>
  toMillis(document?.updatedAt) ||
  toMillis(document?.uploadedAt) ||
  toMillis(document?.reviewedAt) ||
  0;

const getDocumentSlotKey = (document) =>
  `${normalizeDocumentKey(document?.documentType)}:${document?.side || "single"}`;

const getCurrentDocumentsBySlot = (documents = []) => {
  const bySlot = new Map();

  documents.forEach((document) => {
    if (!document?.id && !document?.documentType) return;

    const status = String(document.status || document.reviewStatus || "").toLowerCase();
    if (status && !ACTIVE_DOCUMENT_STATUSES.has(status)) return;

    const slotKey = getDocumentSlotKey(document);
    const existing = bySlot.get(slotKey);
    if (!existing || getDocumentSortTime(document) >= getDocumentSortTime(existing)) {
      bySlot.set(slotKey, document);
    }
  });

  return Array.from(bySlot.values()).sort(
    (a, b) => getDocumentSortTime(b) - getDocumentSortTime(a),
  );
};

const formatDateOnly = (value) => {
  const millis = toMillis(value);
  if (!millis) return "Not added yet";

  return new Date(millis).toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const extractDocumentUrl = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value.trim();
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const url = extractDocumentUrl(item);
      if (url) return url;
    }
    return "";
  }

  if (typeof value === "object") {
    const directUrl =
      value.signedUrl ||
      value.url ||
      value.downloadURL ||
      value.downloadUrl ||
      value.fileUrl ||
      value.uri;

    if (typeof directUrl === "string" && directUrl.trim()) {
      return directUrl.trim();
    }

    return extractDocumentUrl(value.files || value.items || value.documents || []);
  }

  return "";
};

const findFirstUrl = (source, keys) => {
  for (const key of keys) {
    const url = extractDocumentUrl(source?.[key]);
    if (url) return url;
  }
  return "";
};

const buildLegacyDocuments = (providerData, explicitLegacyDocuments) => ({
  ...(providerData?.documents || {}),
  ...(providerData?.provider_onboarding?.documents || {}),
  ...(providerData?.details?.provider_onboarding?.documents || {}),
  ...(explicitLegacyDocuments || {}),
});

const buildMirroredDocument = (value, fallbackType) => {
  if (!value || typeof value !== "object") return null;

  const documentType = normalizeDocumentKey(value.documentType || fallbackType);
  if (!documentType) return null;

  const side = value.side || "single";
  return {
    id:
      value.id ||
      value.documentId ||
      `${documentType}-${side}-${value.storagePath || value.originalName || "secure"}`,
    documentType: value.documentType || fallbackType,
    side,
    status: value.status || "submitted",
    reviewStatus: value.reviewStatus || "pending_review",
    originalName: value.originalName || value.name || value.filename || "Secure upload",
    mimeType: value.mimeType || "",
    sizeBytes: value.sizeBytes || 0,
    storagePath: value.storagePath || "",
    uploadedAt: value.uploadedAt || null,
    updatedAt: value.updatedAt || null,
  };
};

const collectMirroredDocuments = (target, value, fallbackType = "") => {
  if (!value) return;

  if (Array.isArray(value)) {
    value.forEach((item) => collectMirroredDocuments(target, item, fallbackType));
    return;
  }

  if (typeof value !== "object") return;

  const hasSecureShape =
    value.uploadMode === "secure_v2" ||
    value.storagePath ||
    value.documentId ||
    value.documentIds ||
    value.storagePaths ||
    value.files ||
    value.documentType;
  const hasNestedEntries =
    Array.isArray(value.files) || Array.isArray(value.documents) || Array.isArray(value.items);

  if (Array.isArray(value.files)) {
    value.files.forEach((file) =>
      collectMirroredDocuments(target, file, value.documentType || fallbackType),
    );
  }

  if (Array.isArray(value.documents)) {
    value.documents.forEach((document) =>
      collectMirroredDocuments(target, document, value.documentType || fallbackType),
    );
  }

  if (Array.isArray(value.items)) {
    value.items.forEach((item) =>
      collectMirroredDocuments(target, item, value.documentType || fallbackType),
    );
  }

  if (hasNestedEntries) {
    return;
  }

  if (
    hasSecureShape &&
    (value.id || value.documentId || value.storagePath || value.documentType)
  ) {
    const mirroredDocument = buildMirroredDocument(value, fallbackType);
    if (mirroredDocument) {
      const key = normalizeDocumentKey(mirroredDocument.documentType);
      if (!target[key]) target[key] = [];
      if (!target[key].some((item) => item.id === mirroredDocument.id)) {
        target[key].push(mirroredDocument);
      }
    }
    return;
  }

  Object.entries(value).forEach(([key, nestedValue]) => {
    collectMirroredDocuments(target, nestedValue, normalizeDocumentKey(key) || fallbackType);
  });
};

const buildMirroredDocumentsByType = (legacyMap) => {
  const target = {};

  DOCUMENT_GROUPS.forEach((group) => {
    collectMirroredDocuments(target, legacyMap[group.key], group.key);
  });
  collectMirroredDocuments(target, legacyMap.secureDocuments);

  return target;
};

const resolveIdentityExpiry = (providerData, documentKey) => {
  if (!providerData) return null;

  const onboarding = providerData.provider_onboarding || {};
  const profile = providerData.profile || {};
  const onboardingProfile = onboarding.profile || {};

  const candidates =
    documentKey === "passport"
      ? [
          providerData.passport?.expiry,
          onboarding.passport?.expiry,
          profile.passport?.expiry,
          onboardingProfile.passport?.expiry,
          providerData.passportExpiryTimestamp,
          onboarding.passportExpiryTimestamp,
          providerData.passportExpiry,
          onboarding.passportExpiry,
          profile.passportExpiry,
          onboardingProfile.passportExpiry,
        ]
      : [
          providerData.drivingLicence?.expiry,
          providerData.drivingLicense?.expiry,
          onboarding.drivingLicence?.expiry,
          onboarding.drivingLicense?.expiry,
          profile.drivingLicence?.expiry,
          profile.drivingLicense?.expiry,
          onboardingProfile.drivingLicence?.expiry,
          onboardingProfile.drivingLicense?.expiry,
          providerData.drivingLicenceExpiryTimestamp,
          providerData.drivingLicenseExpiryTimestamp,
          onboarding.drivingLicenceExpiryTimestamp,
          onboarding.drivingLicenseExpiryTimestamp,
          providerData.drivingLicenceExpiry,
          providerData.drivingLicenseExpiry,
          onboarding.drivingLicenceExpiry,
          onboarding.drivingLicenseExpiry,
          profile.drivingLicenceExpiry,
          profile.drivingLicenseExpiry,
          onboardingProfile.drivingLicenceExpiry,
          onboardingProfile.drivingLicenseExpiry,
        ];

  return candidates.find(Boolean) || null;
};

/**
 * Lists secure provider documents for admin review with legacy URL fallback.
 *
 * @param {Object} props
 * @param {string} props.providerId
 * @param {Object} [props.providerData]
 * @param {Object} [props.legacyDocuments]
 * @param {boolean} [props.embedded]
 * @param {boolean} [props.showHeader]
 * @returns {JSX.Element|null}
 */
const ProviderDocumentReviewList = ({
  providerId,
  providerData = null,
  legacyDocuments = null,
  embedded = false,
  showHeader = true,
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadDocuments = async () => {
    if (!providerId) return;

    setLoading(true);
    setError("");

    try {
      const result = await getProviderDocumentsForReview({ providerId });
      const nextDocuments = (result.documents || []).slice().sort((a, b) => {
        const left = toMillis(b.uploadedAt || b.updatedAt);
        const right = toMillis(a.uploadedAt || a.updatedAt);
        return left - right;
      });
      setDocuments(nextDocuments);
    } catch (loadError) {
      setError(loadError.message || "Secure documents could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  const legacyMap = useMemo(
    () => buildLegacyDocuments(providerData, legacyDocuments),
    [providerData, legacyDocuments],
  );

  const currentDocuments = useMemo(
    () => getCurrentDocumentsBySlot(documents),
    [documents],
  );
  const currentDocumentIds = useMemo(
    () => new Set(currentDocuments.map((document) => document.id)),
    [currentDocuments],
  );
  const olderDocumentCount = Math.max(documents.length - currentDocuments.length, 0);

  const secureDocumentsByType = useMemo(() => {
    return currentDocuments.reduce((acc, document) => {
      const key = normalizeDocumentKey(document.documentType);
      if (!acc[key]) acc[key] = [];
      acc[key].push(document);
      return acc;
    }, {});
  }, [currentDocuments]);

  const mirroredDocumentsByType = useMemo(
    () => buildMirroredDocumentsByType(legacyMap),
    [legacyMap],
  );

  const knownSecureDocumentIds = new Set(
    DOCUMENT_GROUPS.flatMap((group) => secureDocumentsByType[group.key] || []).map(
      (document) => document.id,
    ),
  );
  const otherSecureDocuments = currentDocuments.filter(
    (document) =>
      currentDocumentIds.has(document.id) && !knownSecureDocumentIds.has(document.id),
  );

  if (!providerId) {
    return null;
  }

  const content = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: showHeader ? 2 : 1.5,
        }}
      >
        {showHeader ? (
          <Box>
            <Typography variant="h6">Provider Documents</Typography>
            <Typography variant="body2" color="text.secondary">
              Secure uploads are shown first. Legacy links are used as fallback.
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Secure uploads are preferred over legacy links.
          </Typography>
        )}
        <Button
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={loadDocuments}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error} Legacy links are still shown when available.
        </Alert>
      )}

      {!error && olderDocumentCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Showing the latest upload for each document slot. Older replaced documents are
          kept in history but ignored for application approval.
        </Alert>
      )}

      {loading && documents.length === 0 && Object.keys(legacyMap).length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
          {DOCUMENT_GROUPS.map((group) => {
            const secureDocuments = secureDocumentsByType[group.key] || [];
            const mirroredDocuments = mirroredDocumentsByType[group.key] || [];
            const legacyUrl = findFirstUrl(legacyMap, group.legacyKeys);
            const expiryValue = group.identity
              ? resolveIdentityExpiry(providerData, group.key)
              : null;

            return (
              <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={group.key} sx={{ minWidth: 0 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      mb:
                        secureDocuments.length ||
                        legacyUrl ||
                        mirroredDocuments.length ||
                        group.identity
                          ? 1.5
                          : 0,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>{group.label}</Typography>
                    <Chip
                      size="small"
                      color={
                        secureDocuments.length
                          ? "success"
                          : mirroredDocuments.length
                            ? "info"
                            : legacyUrl
                              ? "primary"
                              : "default"
                      }
                      label={
                        secureDocuments.length
                          ? `${secureDocuments.length} secure`
                          : mirroredDocuments.length
                            ? `${mirroredDocuments.length} secure ref`
                            : legacyUrl
                              ? "Legacy link"
                              : "Not uploaded"
                      }
                    />
                  </Box>

                  {group.identity && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: secureDocuments.length || legacyUrl ? 1.5 : 0,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Expiry Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDateOnly(expiryValue)}
                      </Typography>
                    </Box>
                  )}

                  {secureDocuments.length > 0 ? (
                    <Box sx={{ display: "grid", gap: 1.5 }}>
                      {secureDocuments.map((document) => (
                        <DocumentReviewCard
                          key={document.id}
                          document={document}
                          onReviewed={(updatedDocument) => {
                            setDocuments((current) =>
                              current.map((item) =>
                                item.id === updatedDocument.id
                                  ? { ...item, ...updatedDocument }
                                  : item,
                              ),
                            );
                          }}
                        />
                      ))}
                    </Box>
                  ) : mirroredDocuments.length > 0 ? (
                    <Box sx={{ display: "grid", gap: 1 }}>
                      {mirroredDocuments.map((document) => (
                        <Paper
                          key={document.id}
                          variant="outlined"
                          sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                              {document.originalName}
                            </Typography>
                            <Chip
                              size="small"
                              label={document.reviewStatus || document.status || "submitted"}
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Secure upload reference found. Refresh to load signed review URLs.
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  ) : legacyUrl ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Uploaded
                      </Typography>
                      <Button size="small" href={legacyUrl} target="_blank" rel="noreferrer">
                        Open file
                      </Button>
                    </Box>
                  ) : (
                    !group.identity && (
                      <Typography variant="body2" color="text.disabled" sx={{ fontStyle: "italic" }}>
                        Not uploaded yet
                      </Typography>
                    )
                  )}
                </Paper>
              </Grid>
            );
          })}

          {otherSecureDocuments.map((document) => (
            <Grid item size={{ xs: 12, md: 6, lg: 4 }} key={document.id} sx={{ minWidth: 0 }}>
              <DocumentReviewCard
                document={document}
                onReviewed={(updatedDocument) => {
                  setDocuments((current) =>
                    current.map((item) =>
                      item.id === updatedDocument.id ? { ...item, ...updatedDocument } : item,
                    ),
                  );
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && documents.length === 0 && Object.keys(legacyMap).length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No provider documents have been submitted yet.
        </Alert>
      )}
    </>
  );

  if (embedded) {
    return <Box sx={{ minWidth: 0, width: "100%" }}>{content}</Box>;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 2, minWidth: 0 }}>
      {content}
    </Paper>
  );
};

export default ProviderDocumentReviewList;
